<header class="flex header">
  <h2>
    @if (isEditing) {
      {{ 'TaxAuthorization_Edit_Title' | translateWF: 'Edit Tax Authorization' }}
    } @else {
      {{ 'TaxAuthorization_Create_Title' | translateWF: 'New Tax Authorization' }}
    }
  </h2>
  <p-button icon="pi pi-times" (click)="onClose()" />
</header>

<section class="content">
  <form [formGroup]="form">
    <section class="flow">
      <!-- Holder — always supplied by the caller (the business on screen, or the site's own company). -->
      <div class="form-box">
        <label>{{ 'Cai_Business' | translateWF: 'Business' }}</label>
        <input pInputText class="w-full" [value]="businessName ?? ''" readonly />
        <small class="text-surface-500">
          @if (isSelfIssued) {
            {{ 'TaxAuthorization_Holder_Self_Hint' | translateWF: 'This range is issued by your own company.' }}
          } @else {
            {{ 'TaxAuthorization_Holder_Provider_Hint' | translateWF: "The provider's own authorization, used to validate their invoice numbers." }}
          }
        </small>
      </div>

      <!-- Which document the range covers. All four for our own company; a provider's covers AP only. -->
      <div class="form-box">
        <label for="authorization-entity">{{ 'TaxAuthorization_Entity' | translateWF: 'Document' }}</label>
        <p-select
          inputId="authorization-entity"
          formControlName="entityDefinitionCode"
          [options]="entityDefinitionOptions"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          styleClass="w-full"
          (onChange)="onEntityDefinitionChange()"
        />
        @if (isEditing) {
          <small class="text-surface-500">
            {{ 'TaxAuthorization_Entity_Locked_Hint' | translateWF: 'Fixed once the range exists — its correlativo belongs to this document.' }}
          </small>
        } @else if (!isSelfIssued) {
          <small class="text-surface-500">
            {{ 'TaxAuthorization_Entity_Provider_Hint' | translateWF: 'A provider authorization only ever covers AP documents.' }}
          </small>
        }
      </div>

      <!-- Covered documents — AP ranges only. One authorization commonly covers several. -->
      @if (isApDocumentRange) {
        <div class="form-box">
          <label for="cai-doctype">{{ 'Cai_DocumentTypes' | translateWF: 'Covers' }}</label>
          <p-multiSelect
            inputId="cai-doctype"
            formControlName="documentTypes"
            [options]="documentTypeOptions"
            optionLabel="label"
            optionValue="value"
            display="chip"
            appendTo="body"
            styleClass="w-full"
            [placeholder]="'Cai_DocumentTypes_Placeholder' | translateWF: 'Select the documents this covers'"
          />
          <small class="text-surface-500">
            @if (isSelfIssued) {
              {{ 'Cai_DocumentTypes_Hint' | translateWF: 'Which fiscal documents this range numbers.' }}
            } @else {
              {{ 'Cai_DocumentTypes_Provider_Hint' | translateWF: 'What the authority granted this CAI for — usually Invoice and Receipt together; a note normally has its own.' }}
            }
          </small>
        </div>
      }

      <!-- Country (#4791) — selects the prefix shape and the padding the regime accepts. -->
      <div class="form-box">
        <label for="authorization-country">{{ 'TaxAuthorization_Country' | translateWF: 'Country' }}</label>
        <p-select
          inputId="authorization-country"
          formControlName="countryCode"
          [options]="countryOptions"
          optionLabel="label"
          optionValue="value"
          styleClass="w-full"
          appendTo="body"
          (onChange)="onCountryChange($event.value)"
        />
        <small class="text-surface-500">
          {{ 'TaxAuthorization_Country_Hint' | translateWF: 'Determines the authorized range format.' }}
        </small>
      </div>

      <!-- Authorization number -->
      <div class="form-box">
        <label for="authorization-number">{{ 'TaxAuthorization_Number' | translateWF: 'Authorization Number' }}</label>
        <input pInputText formControlName="authorizationNumber" id="authorization-number" class="w-full" />
      </div>

      <!-- Prefix — the series shared by every number in the band. -->
      <div class="form-box">
        <label for="authorization-prefix">{{ 'TaxAuthorization_Prefix' | translateWF: 'Prefix' }}</label>
        <input
          pInputText
          formControlName="prefix"
          id="authorization-prefix"
          class="w-full font-mono"
          [placeholder]="prefixHint()"
          [ngClass]="{ 'ng-invalid ng-dirty': form.controls.prefix.invalid && form.controls.prefix.touched }"
        />
        @if (form.controls.prefix.touched && form.controls.prefix.hasError('pattern')) {
          <small class="text-red-500">
            {{ 'TaxAuthorization_Prefix_Format' | translateWF: 'Prefix must look like' }} <span class="font-mono">{{ prefixHint() }}</span>
          </small>
        } @else {
          <small class="text-surface-500">
            {{ 'TaxAuthorization_Prefix_Hint' | translateWF: 'Include the trailing separator, e.g.' }} <span class="font-mono">{{ prefixHint() }}</span>
          </small>
        }
      </div>

      <!-- Padding — the country's printed width. Shown, not chosen: the API resolves it from the country
           and would ignore anything sent here. -->
      <div class="form-box">
        <label for="authorization-padding">{{ 'TaxAuthorization_Padding' | translateWF: 'Number padding' }}</label>
        <input pInputText id="authorization-padding" class="w-full" [value]="form.controls.sequenceLength.value ?? ''" readonly />
        <small class="text-surface-500">
          {{ 'TaxAuthorization_Padding_Hint' | translateWF: 'Set by the country — 2801 is issued as 00002801.' }}
        </small>
      </div>

      <!-- Numeric band. These are plain numbers; the formatted range is shown below. -->
      <div class="grid-2 gap-3">
        <div class="form-box">
          <label for="authorization-range-start">{{ 'TaxAuthorization_RangeStart' | translateWF: 'From' }}</label>
          <p-inputNumber
            inputId="authorization-range-start"
            formControlName="rangeStart"
            [min]="0"
            [max]="maxSequence() ?? undefined"
            [useGrouping]="false"
            [maxlength]="form.controls.sequenceLength.value"
            styleClass="w-full"
            [inputStyleClass]="'w-full'"
            placeholder="2801"
          />
        </div>
        <div class="form-box">
          <label for="authorization-range-end">{{ 'TaxAuthorization_RangeEnd' | translateWF: 'To' }}</label>
          <p-inputNumber
            inputId="authorization-range-end"
            formControlName="rangeEnd"
            [min]="0"
            [max]="maxSequence() ?? undefined"
            [useGrouping]="false"
            [maxlength]="form.controls.sequenceLength.value"
            styleClass="w-full"
            [inputStyleClass]="'w-full'"
            placeholder="3800"
          />
        </div>
      </div>

      @if (form.hasError('bandOrder')) {
        <small class="text-red-500">{{ 'TaxAuthorization_Band_Order' | translateWF: 'The end of the range must not be lower than the start.' }}</small>
      } @else if (form.controls.rangeStart.hasError('max') || form.controls.rangeEnd.hasError('max')) {
        <small class="text-red-500">
          {{ 'TaxAuthorization_Band_Max' | translateWF: 'Range values cannot exceed' }} {{ maxSequence() }} ({{ form.controls.sequenceLength.value }}
          {{ 'TaxAuthorization_Digits' | translateWF: 'digits' }}).
        </small>
      }

      <!-- What will actually be issued. The parts above are easy to get subtly wrong; this is the only
           place the operator sees the numbers the way they will appear on a document. -->
      @if (rangePreview(); as preview) {
        <div class="form-box">
          <label>{{ 'TaxAuthorization_RangePreview' | translateWF: 'Authorized range' }}</label>
          <div class="rounded border border-[var(--p-content-border-color)] bg-[var(--p-content-hover-background)] px-3 py-2">
            <div class="font-mono text-sm">{{ preview.start }} – {{ preview.end }}</div>
            @if (bandSize(); as size) {
              <div class="text-xs text-[var(--p-text-muted-color)] mt-1">{{ size | number }} {{ 'TaxAuthorization_NumbersAvailable' | translateWF: 'numbers' }}</div>
            }
          </div>
        </div>
      }

      <!-- Start date -->
      <div class="form-box">
        <label for="cai-start-date">{{ 'Cai_StartDate' | translateWF: 'Start Date' }}</label>
        <p-datepicker inputId="cai-start-date" formControlName="startDate" appendTo="body" styleClass="w-full" [showIcon]="true" />
      </div>

      <!-- End date -->
      <div class="form-box">
        <label for="cai-end-date">{{ 'Cai_EndDate' | translateWF: 'End Date' }}</label>
        <p-datepicker
          inputId="cai-end-date"
          formControlName="endDate"
          appendTo="body"
          styleClass="w-full"
          [showIcon]="true"
          [ngClass]="{ 'ng-invalid ng-dirty': form.hasError('dateOrder') && form.controls.endDate.touched }"
        />
        @if (form.hasError('dateOrder') && form.controls.endDate.touched) {
          <small class="text-red-500">{{ 'Cai_Date_Order' | translateWF: 'End date must be after the start date' }}</small>
        }
      </div>

      <!-- Active -->
      <div class="form-box">
        <div class="row-sm-start-center">
          <p-checkbox formControlName="active" [binary]="true" inputId="cai-active" />
          <label for="cai-active" class="cursor-pointer">{{ 'Cai_Active' | translateWF: 'Active' }}</label>
        </div>
      </div>
    </section>
  </form>
</section>

<footer>
  <p-button class="w-full" [label]="'Save' | translateWF: 'Save'" [disabled]="form.invalid || saving" [loading]="saving" (click)="onSave()" />
  <p-button class="w-full" [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" [disabled]="saving" (click)="onClose()" />
</footer>
